import React, { useState, useEffect } from "react";
import { Edit, Plus, CheckCircle, Package, X } from "lucide-react";

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [formData, setFormData] = useState({
    packageName: "",
    price: "",
    validity: "",
    credit: "", // Added credit field
    pointOne: "",
    pointTwo: "",
    pointThree: "",
    pointFour: "",
    pointFive: "",
    pointSix: "",
    pointSeven: "",
    pointEight: "",
    pointNine: "",
    pointTen: "",
  });

  // Fetch all packages
  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://allinone-clone-7d5d795e98ce.herokuapp.com/packages`);
      if (!response.ok) {
        throw new Error("Failed to fetch packages");
      }
      const data = await response.json();
      setPackages(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch a single package
  const fetchPackage = async (id) => {
    try {
      const response = await fetch(`https://allinone-clone-7d5d795e98ce.herokuapp.com/package/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch package");
      }
      const data = await response.json();
      setCurrentPackage(data);
      setFormData(data);
      setShowEditForm(true);
    } catch (err) {
      setError(err.message);
    }
  };

  // Add a new package
  const addPackage = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch("https://allinone-clone-7d5d795e98ce.herokuapp.com/add-package", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error("Failed to add package");
      }
      setShowAddForm(false);
      setFormData({
        packageName: "",
        price: "",
        validity: "",
        credit: "", // Reset credit field
        pointOne: "",
        pointTwo: "",
        pointThree: "",
        pointFour: "",
        pointFive: "",
        pointSix: "",
        pointSeven: "",
        pointEight: "",
        pointNine: "",
        pointTen: "",
      });
      fetchPackages();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update a package
  const updatePackage = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `https://allinone-clone-7d5d795e98ce.herokuapp.com/edit-package/${currentPackage._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update package");
      }
      setShowEditForm(false);
      setCurrentPackage(null);
      fetchPackages();
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Render package features as list items
  const renderFeatures = (pkg) => {
    const features = [
      pkg.pointOne,
      pkg.pointTwo,
      pkg.pointThree,
      pkg.pointFour,
      pkg.pointFive,
      pkg.pointSix,
      pkg.pointSeven,
      pkg.pointEight,
      pkg.pointNine,
      pkg.pointTen,
    ].filter(Boolean);
    return features.map((feature, index) => (
      <li key={index} className="mb-2 d-flex align-items-center gap-2">
        <CheckCircle size={16} className="text-success" />
        {feature}
      </li>
    ));
  };

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold d-flex align-items-center gap-2">
          <Package size={24} />
          Pricing Packages
        </h3>
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-2"
            onClick={() => setShowAddForm(true)}
            disabled={packages.length >= 3}
            title={packages.length >= 3 ? "Maximum 3 packages allowed" : ""}
          >
            <Plus size={18} /> Add Package
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden"></span>
          </div>
          <p className="mt-3">Loading packages...</p>
        </div>
      ) : (
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {packages.map((pkg) => (
            <div key={pkg._id} className="col">
              <div className="card h-100 shadow-sm">
                <div className="card-header bg-primary text-white text-center py-3">
                  <h3 className="mb-0">{pkg.packageName}</h3>
                </div>
                <div className="card-body d-flex flex-column">
                  <div className="text-center mb-4">
                    <h2 className="display-5 fw-bold text-dark">
                      ${pkg.price}
                    </h2>
                    <p className="text-muted">{pkg.credit} Credits</p>
                  </div>
                  <ul className="list-unstyled mb-4">{renderFeatures(pkg)}</ul>
                  <div className="mt-auto text-center">
                    <button
                      className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 mx-auto"
                      onClick={() => fetchPackage(pkg._id)}
                    >
                      <Edit size={16} /> Edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {packages.length === 0 && !loading && (
            <div className="col-12 text-center py-5">
              <p className="text-muted">
                No packages available. Add a new package to get started.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Add Package Form */}
      {showAddForm && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Add New Package</h5>
                <button
                  type="button"
                  className="btn btn-link text-white p-0 m-0"
                  onClick={() => setShowAddForm(false)}
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={addPackage}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="packageName" className="form-label">
                        Package Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="packageName"
                        name="packageName"
                        value={formData.packageName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="price" className="form-label">
                        Price
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="credit" className="form-label">
                        Credits
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="credit"
                        name="credit"
                        value={formData.credit}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointOne" className="form-label">
                        Feature 1
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointOne"
                        name="pointOne"
                        value={formData.pointOne}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointTwo" className="form-label">
                        Feature 2
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointTwo"
                        name="pointTwo"
                        value={formData.pointTwo}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointThree" className="form-label">
                        Feature 3
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointThree"
                        name="pointThree"
                        value={formData.pointThree}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointFour" className="form-label">
                        Feature 4
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointFour"
                        name="pointFour"
                        value={formData.pointFour}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointFive" className="form-label">
                        Feature 5
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointFive"
                        name="pointFive"
                        value={formData.pointFive}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointSix" className="form-label">
                        Feature 6
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointSix"
                        name="pointSix"
                        value={formData.pointSix}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointSeven" className="form-label">
                        Feature 7
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointSeven"
                        name="pointSeven"
                        value={formData.pointSeven}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointEight" className="form-label">
                        Feature 8
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointEight"
                        name="pointEight"
                        value={formData.pointEight}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointNine" className="form-label">
                        Feature 9
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointNine"
                        name="pointNine"
                        value={formData.pointNine}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointTen" className="form-label">
                        Feature 10
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointTen"
                        name="pointTen"
                        value={formData.pointTen}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => setShowAddForm(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save Package
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Package Form */}
      {showEditForm && currentPackage && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Edit Package</h5>
                <button
                  type="button"
                  className="btn btn-link text-white p-0 m-0"
                  onClick={() => setShowEditForm(false)}
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={updatePackage}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="packageName" className="form-label">
                        Package Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="packageName"
                        name="packageName"
                        value={formData.packageName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="price" className="form-label">
                        Price
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="credit" className="form-label">
                        Credits
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="credit"
                        name="credit"
                        value={formData.credit}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointOne" className="form-label">
                        Feature 1
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointOne"
                        name="pointOne"
                        value={formData.pointOne}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointTwo" className="form-label">
                        Feature 2
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointTwo"
                        name="pointTwo"
                        value={formData.pointTwo}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointThree" className="form-label">
                        Feature 3
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointThree"
                        name="pointThree"
                        value={formData.pointThree}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointFour" className="form-label">
                        Feature 4
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointFour"
                        name="pointFour"
                        value={formData.pointFour}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointFive" className="form-label">
                        Feature 5
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointFive"
                        name="pointFive"
                        value={formData.pointFive}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointSix" className="form-label">
                        Feature 6
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointSix"
                        name="pointSix"
                        value={formData.pointSix}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointSeven" className="form-label">
                        Feature 7
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointSeven"
                        name="pointSeven"
                        value={formData.pointSeven}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointEight" className="form-label">
                        Feature 8
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointEight"
                        name="pointEight"
                        value={formData.pointEight}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointNine" className="form-label">
                        Feature 9
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointNine"
                        name="pointNine"
                        value={formData.pointNine}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="pointTen" className="form-label">
                        Feature 10
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pointTen"
                        name="pointTen"
                        value={formData.pointTen}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => setShowEditForm(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Update Package
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Packages;
